<template>
    <BModal
        :model-value="modal"
        centered
        scrollable
        size="xl"
        @update:modelValue="updateModal"
        no-close-on-backdrop
    >
        <!-- Header -->
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between w-100"
            >
                <h5 class="mb-0">Program Detail</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <!-- Program Info -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">Program Name</span>
                <p>{{ props.selectedData?.name ?? "-" }}</p>
            </BCol>
            <BCol class="text-start">
                <span class="fw-bold">Pax</span>
                <p>{{ props.selectedData?.pax ?? "-" }}</p>
            </BCol>
            <BCol class="text-start">
                <span class="fw-bold">From</span>
                <p>
                    {{
                        props.selectedData?.from_destination
                            ? `${props.selectedData.from_destination.name} (${props.selectedData.from_destination.code})`
                            : "-"
                    }}
                </p>
            </BCol>
            <BCol class="text-start">
                <span class="fw-bold">To</span>
                <p>
                    {{
                        props.selectedData?.to_destination
                            ? `${props.selectedData.to_destination.name} (${props.selectedData.to_destination.code})`
                            : "-"
                    }}
                </p>
            </BCol>
        </BRow>

        <!-- 1. Transportation -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">1. Transportation</span>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-secondary">
                            <tr>
                                <th width="1" class="text-left">No</th>
                                <th class="text-left">Vehicle Name</th>
                                <th class="text-left">Vehicle Type</th>
                                <th class="text-center">Pax</th>
                                <th class="text-right col-sm-2">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in props.selectedData
                                    ?.transportation"
                                :key="index"
                            >
                                <td class="text-left">
                                    {{ index + 1 }}
                                </td>
                                <td class="text-left">
                                    {{ item.vehicle.name }}
                                </td>
                                <td class="text-left">
                                    {{ item.vehicle.type }}
                                </td>
                                <td class="text-center">
                                    {{ item.pax }}
                                </td>
                                <td class="text-right">
                                    {{ formatRupiah(item.price) }}
                                </td>
                            </tr>
                            <tr
                                v-if="
                                    !props.selectedData?.transportation?.length
                                "
                            >
                                <td colspan="5" class="text-center">
                                    No data available
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </BCol>
        </BRow>

        <!-- 2. Accommodation -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">2. Accommodation</span>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-secondary">
                            <tr>
                                <th width="1" class="text-left">No</th>
                                <th class="text-left">Destination</th>
                                <th class="text-left">Hotel</th>
                                <th class="text-left">RoomType</th>
                                <th class="text-center">Pax</th>
                                <th class="text-right">Price</th>
                                <th class="text-center">Extra Bed</th>
                                <th class="text-right">Extra Bed Price</th>
                                <th class="text-right col-sm-2">Total Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in props.selectedData
                                    ?.accommodation"
                                :key="index"
                            >
                                <td class="text-left">
                                    {{ index + 1 }}
                                </td>
                                <td class="text-left">
                                    {{
                                        item.destination.name +
                                        "(" +
                                        item.destination.code +
                                        ")"
                                    }}
                                </td>
                                <td class="text-left">
                                    {{ item.name }} ({{ item.code }})
                                </td>
                                <td class="text-left">
                                    {{ item.room_type }}
                                </td>
                                <td class="text-center">
                                    {{ item.pax }}
                                </td>
                                <td class="text-right">
                                    {{ formatRupiah(item.price) }}
                                </td>
                                <td class="text-center">
                                    <span
                                        :class="`badge badge-status text-${getBadgeExtraBed(
                                            item.extra_bed
                                        )} border border-${getBadgeExtraBed(
                                            item.extra_bed
                                        )}`"
                                    >
                                        {{ item.extra_bed ? "Yes" : "No" }}
                                    </span>
                                </td>
                                <td class="text-right">
                                    {{ formatRupiah(item.extra_bed_price) }}
                                </td>
                                <td class="text-right">
                                    {{ formatRupiah(item.total_price) }}
                                </td>
                            </tr>
                            <tr
                                v-if="
                                    !props.selectedData?.accommodation?.length
                                "
                            >
                                <td colspan="9" class="text-center">
                                    No data available
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </BCol>
        </BRow>

        <!-- 3. Activity -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">3. Activity</span>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-secondary">
                            <tr>
                                <th width="1" class="text-left">No</th>
                                <th class="text-left">Destination</th>
                                <th class="text-left">Activity Name</th>
                                <th class="text-center">Pax</th>
                                <th class="text-right col-sm-2">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in props.selectedData
                                    ?.activity"
                                :key="index"
                            >
                                <td class="text-left">
                                    {{ index + 1 }}
                                </td>
                                <td class="text-left">
                                    {{
                                        item.destination.name +
                                        "(" +
                                        item.destination.code +
                                        ")"
                                    }}
                                </td>
                                <td class="text-left">
                                    {{ item.name }}
                                </td>
                                <td class="text-center text-wrap">
                                    {{ item.pax }}
                                </td>
                                <td class="text-right">
                                    {{ formatRupiah(item.price) }}
                                </td>
                            </tr>
                            <tr v-if="!props.selectedData?.activity?.length">
                                <td colspan="5" class="text-center">
                                    No data available
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </BCol>
        </BRow>

        <!-- 4. Meals -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">4. Meals</span>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-secondary">
                            <tr>
                                <th width="1" class="text-left">No</th>
                                <th class="text-left">Name</th>
                                <th class="text-right col-sm-2">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in props.selectedData
                                    ?.meals"
                                :key="index"
                            >
                                <td>{{ index + 1 }}</td>
                                <td>{{ item.name }}</td>
                                <td class="text-right">
                                    {{ formatRupiah(item.price) }}
                                </td>
                            </tr>
                            <tr v-if="!props.selectedData?.meals?.length">
                                <td colspan="3" class="text-center">
                                    No data available
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </BCol>
        </BRow>

        <!-- Total Price -->
        <BRow>
            <BCol>
                <div class="table-responsive">
                    <table class="table table-sm">
                        <tbody>
                            <tr style="font-size: 17px">
                                <td class="fw-bold" colspan="2">Total Price</td>
                                <td
                                    class="text-end fw-bold fw-bold bg-success text-white col-sm-2"
                                >
                                    {{
                                        props.selectedData?.price
                                            ? formatRupiah(
                                                  props.selectedData.price
                                              )
                                            : "Rp 0"
                                    }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </BCol>
        </BRow>

        <!-- Inclusion -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">Inclusion</span>
                <ul v-if="props.selectedData?.inclusion">
                    <li
                        v-for="(
                            item, index
                        ) in props.selectedData.inclusion.split('\n')"
                        :key="index"
                    >
                        {{ item }}
                    </li>
                </ul>
                <p v-else>-</p>
            </BCol>
        </BRow>

        <!-- Exclusion -->
        <BRow class="mb-4">
            <BCol class="text-start">
                <span class="fw-bold">Exclusion</span>
                <ul v-if="props.selectedData?.exclusion">
                    <li
                        v-for="(
                            item, index
                        ) in props.selectedData.exclusion.split('\n')"
                        :key="index"
                    >
                        {{ item }}
                    </li>
                </ul>
                <p v-else>-</p>
            </BCol>
        </BRow>

        <!-- Footer -->
        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="danger" @click="updateModal(false)"
                    >Close</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";
import { BModal, BRow, BCol, BButton } from "bootstrap-vue-next";
import { formatRupiah } from "@/utils/format";

const props = defineProps({
    action: String,
    modal: Boolean,
    selectedData: Object,
});

const emit = defineEmits(["update:modal"]);

const updateModal = (value) => emit("update:modal", value);

const getBadgeExtraBed = (val) => (val ? "success" : "secondary");
</script>
